import React from 'react';
import { Block, Workflow } from '../types';
import { PromptTestModal } from './PromptTestModal';
import { DeleteBlockModal } from './DeleteBlockModal';
import DecisionBlockConfigPanel from './DecisionBlockConfigPanel';
import PromptOptimizationModal from './PromptOptimizationModal';
import { api } from '../api';
import { Autocomplete, TextField } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

interface BlockLibraryProps {
  blocks: Block[];
  onCreateBlock: (block: Omit<Block, 'id'>) => Promise<Block>;
  onUpdateBlock: (id: string, block: Block) => Promise<Block>;
  onDeleteBlock: (id: string) => Promise<void>;
}

export const BlockLibrary: React.FC<BlockLibraryProps> = ({ blocks, onCreateBlock, onUpdateBlock, onDeleteBlock }) => {
  const [isCreating, setIsCreating] = React.useState(false);
  const [editingBlock, setEditingBlock] = React.useState<Block | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = React.useState(false);
  const [resetConfirmation, setResetConfirmation] = React.useState('');
  const [isResetting, setIsResetting] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  
  // Filter out special blocks, apply search filter, and sort alphabetically
  const displayBlocks = blocks
    .filter(block => {
      // First filter out special blocks (control flow blocks)
      if (block.name.toLowerCase() === 'start' || 
          block.name.toLowerCase() === 'results' ||
          block.name.toLowerCase() === 'decision router' ||
          block.id === 'decision-router') {
        return false;
      }
      
      // Then apply search filter if search term exists
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        block.name.toLowerCase().includes(searchLower) ||
        block.description.toLowerCase().includes(searchLower) ||
        block.category.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Sort alphabetically by block name (case-insensitive)
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

  const [formData, setFormData] = React.useState<Omit<Block, 'id'>>({
    name: '',
    description: '',
    category: '',
    prompt: '',
    model: '', // Will be set by useEffect when models load
    inputs: [],
    outputs: []
  });
  
  // Add ref for the editor form
  const editorFormRef = React.useRef<HTMLFormElement>(null);
  
  // Add state for test modal
  const [isTestModalOpen, setIsTestModalOpen] = React.useState(false);
  const [testBlockData, setTestBlockData] = React.useState<{id: string, name: string, prompt: string, model?: string}>({
    id: '',
    name: '',
    prompt: '',
    model: undefined
  });
  
  // Add state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [blockToDelete, setBlockToDelete] = React.useState<Block | null>(null);
  const [usedInWorkflows, setUsedInWorkflows] = React.useState<Workflow[]>([]);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Add state for optimization modal
  const [optimizationModalOpen, setOptimizationModalOpen] = React.useState(false);

  // Model options for dropdown
  const [modelOptions, setModelOptions] = React.useState<{id: string, name: string, provider?: string, displayName?: string, isDefault?: boolean, deprecated?: boolean}[]>([]);

  // Load enabled models on component mount
  React.useEffect(() => {
    const loadEnabledModels = async () => {
      try {
        console.log('Loading enabled models...');
      const response = await api.getAllModels();
      const models = response.models || [];
      console.log('Loaded models:', models.length);
      
      const modelOptions = models.map((model: any) => ({
        id: model.id,
        name: model.name,
        provider: model.provider || 'unknown',
        displayName: `${(model.provider || 'unknown').toUpperCase()} / ${model.name}`,
        isDefault: model.isDefault || false,
        deprecated: model.deprecated || false
      }));
      setModelOptions(modelOptions);
        
        // Find the default model
        const defaultModel = models.find((model: any) => model.isDefault);
        const defaultModelId = defaultModel ? defaultModel.id : (modelOptions.length > 0 ? modelOptions[0].id : '');
        
        console.log('Default model found:', defaultModel?.name, 'ID:', defaultModelId);
        
        // Update form data to use the default model
        setFormData(prev => {
          console.log('Updating form data model from', prev.model, 'to', defaultModelId);
          return {
            ...prev,
            model: defaultModelId
          };
        });
        
      } catch (error) {
        console.error('Failed to load enabled models:', error);
        // Keep fallback models
      }
    };
    
    loadEnabledModels();
  }, []);

  const handleDemoBlocks = async () => {
    // Clear any previous errors
    setError(null);
    // Set loading state
    setIsLoading(true);
    
    try {
      // Create blocks in smaller batches to avoid overwhelming the backend
      console.log("Creating demo blocks...");
      
      const demoBlocks = [
        // Economics blocks
        {
          name: "Policy Impact Analyzer",
          description: "Analyzes potential impacts of policy changes on economic indicators",
          category: "Economics",
          prompt: "Analyze the potential economic impacts of the proposed policy change. Consider effects on GDP, employment, inflation, and income distribution. Provide a structured analysis with quantitative estimates where possible.",
          model: "gpt-4o",
          inputs: ["policy_description"],
          outputs: ["impact_analysis"]
        },
        {
          name: "Economic Indicator Forecaster",
          description: "Predicts key economic indicators using historical data and current trends",
          category: "Economics",
          prompt: "Based on the provided historical data and current economic conditions, forecast the future values of key economic indicators. Include confidence intervals and identify key factors influencing the predictions.",
          model: "gpt-4o",
          inputs: ["historical_data", "current_conditions"],
          outputs: ["forecasts", "confidence_intervals"]
        },
        {
          name: "Market Structure Analyzer",
          description: "Evaluates market competition and structure",
          category: "Economics",
          prompt: "Analyze the market structure and competitive dynamics based on the provided market data. Identify market concentration, entry barriers, and competitive advantages. Calculate relevant market structure metrics.",
          model: "gpt-4o",
          inputs: ["market_data"],
          outputs: ["market_analysis", "concentration_metrics"]
        },
        {
          name: "Fiscal Multiplier Calculator",
          description: "Estimates economic impacts of government spending changes",
          category: "Economics",
          prompt: "Calculate the fiscal multiplier effects of the proposed government spending change. Consider both direct and indirect effects on GDP, employment, and private sector activity.",
          model: "gpt-4o",
          inputs: ["spending_change", "economic_conditions"],
          outputs: ["multiplier_effects"]
        },
        {
          name: "Distributional Impact Assessor",
          description: "Analyzes policy effects across different income groups",
          category: "Economics",
          prompt: "Assess how the proposed policy affects different income groups. Calculate distributional impacts and changes in inequality metrics. Consider both direct and indirect effects.",
          model: "gpt-4o",
          inputs: ["policy_details", "population_data"],
          outputs: ["distributional_analysis", "inequality_metrics"]
        },
        {
          name: "Trade Flow Analyzer",
          description: "Analyzes international trade patterns and impacts",
          category: "Economics",
          prompt: "Analyze international trade flows and their economic impacts. Consider comparative advantages, trade balances, and welfare effects. Calculate relevant trade metrics.",
          model: "gpt-4o",
          inputs: ["trade_data"],
          outputs: ["trade_analysis", "welfare_effects"]
        },
        {
          name: "Labor Market Dynamics Simulator",
          description: "Simulates labor market responses to economic changes",
          category: "Economics",
          prompt: "Simulate how the labor market will respond to the specified economic changes. Consider employment, wages, labor force participation, and skill matching dynamics.",
          model: "gpt-4o",
          inputs: ["economic_changes", "labor_market_data"],
          outputs: ["labor_market_impacts"]
        },
        {
          name: "Regulatory Impact Evaluator",
          description: "Assesses costs and benefits of regulatory changes",
          category: "Economics",
          prompt: "Evaluate the economic costs and benefits of the proposed regulatory change. Consider compliance costs, market effects, and social welfare impacts. Provide quantitative estimates where possible.",
          model: "gpt-4o",
          inputs: ["regulation_details"],
          outputs: ["cost_benefit_analysis"]
        },
        {
          name: "Monetary Policy Response Predictor",
          description: "Predicts central bank responses to economic conditions",
          category: "Economics",
          prompt: "Predict likely monetary policy responses based on current economic conditions and central bank objectives. Consider inflation, employment, and financial stability targets.",
          model: "gpt-4o",
          inputs: ["economic_conditions", "central_bank_objectives"],
          outputs: ["policy_predictions"]
        },
        {
          name: "Public Finance Optimizer",
          description: "Optimizes government revenue and spending decisions",
          category: "Economics",
          prompt: "Optimize public finance decisions considering revenue sources, spending priorities, and fiscal constraints. Analyze trade-offs and provide recommendations for fiscal policy.",
          model: "gpt-4o",
          inputs: ["fiscal_data", "policy_objectives"],
          outputs: ["optimization_results", "policy_recommendations"]
        },
        // NLP blocks
        {
          name: "Text Tokenization",
          description: "Splits text into individual tokens/words while preserving important linguistic features",
          category: "NLP Processing",
          prompt: "Split the input text into tokens while preserving important linguistic features. Return the tokens as a JSON array.",
          model: "",
          inputs: ["text"],
          outputs: ["tokens"]
        },
        {
          name: "Named Entity Recognition",
          description: "Identifies and classifies named entities (people, organizations, locations, etc.) in text",
          category: "NLP AI",
          prompt: "Identify and classify named entities in the input text. Return a JSON array of entities with their types.",
          model: "",
          inputs: ["text"],
          outputs: ["entities"]
        },
        {
          name: "Sentiment Analysis",
          description: "Analyzes the sentiment/emotion of text (positive, negative, neutral)",
          category: "NLP AI",
          prompt: "Analyze the sentiment of the input text. Return a JSON object with sentiment score and label.",
          model: "",
          inputs: ["text"],
          outputs: ["sentiment"]
        },
        {
          name: "Text Summarization",
          description: "Generates a concise summary of longer text while preserving key information",
          category: "NLP AI",
          prompt: "Generate a concise summary of the input text while preserving key information.",
          model: "",
          inputs: ["text"],
          outputs: ["summary"]
        },
        {
          name: "Language Detection",
          description: "Identifies the language of input text",
          category: "NLP Processing",
          prompt: "Identify the language of the input text. Return the language code as a string.",
          model: "",
          inputs: ["text"],
          outputs: ["language"]
        },
        {
          name: "Text Classification",
          description: "Classifies text into predefined categories or topics",
          category: "NLP AI",
          prompt: "Classify the input text into categories. Return a JSON array of categories with confidence scores.",
          model: "",
          inputs: ["text"],
          outputs: ["categories"]
        },
        {
          name: "Keyword Extraction",
          description: "Extracts important keywords and phrases from text",
          category: "NLP Processing",
          prompt: "Extract important keywords and phrases from the input text. Return a JSON array of keywords.",
          model: "",
          inputs: ["text"],
          outputs: ["keywords"]
        },
        {
          name: "Text Similarity",
          description: "Calculates similarity between two text inputs",
          category: "NLP Processing",
          prompt: "Calculate the similarity between two input texts. Return a similarity score between 0 and 1.",
          model: "",
          inputs: ["text1", "text2"],
          outputs: ["similarity"]
        },
        {
          name: "Grammar Correction",
          description: "Corrects grammar and spelling errors in text",
          category: "NLP AI",
          prompt: "Correct grammar and spelling errors in the input text. Return both the corrected text and a list of corrections made.",
          model: "",
          inputs: ["text"],
          outputs: ["corrected_text", "corrections"]
        },
        {
          name: "Text Translation",
          description: "Translates text between languages",
          category: "NLP AI",
          prompt: "Translate the input text to the target language. Return the translated text as a string.",
          model: "",
          inputs: ["text", "target_language"],
          outputs: ["translated_text"]
        },
        // Software Engineering blocks
        {
          name: "User Story Generator",
          description: "Generates well-structured user stories from requirements",
          category: "Software Engineering",
          prompt: "Generate well-structured user stories from the provided requirement or feature description. Follow the format 'As a [user type], I want [capability] so that [benefit]'. Include acceptance criteria for each user story.",
          model: "gpt-4o",
          inputs: ["requirement"],
          outputs: ["user_stories"]
        },
        {
          name: "Acceptance Criteria Validator",
          description: "Validates and improves acceptance criteria for user stories",
          category: "Software Engineering",
          prompt: "Analyze the provided acceptance criteria for completeness, clarity, and testability. Identify any issues and provide improved versions.",
          model: "gpt-4o",
          inputs: ["acceptance_criteria"],
          outputs: ["validated_criteria"]
        },
        {
          name: "Test Case Generator",
          description: "Generates comprehensive test cases from requirements",
          category: "Software Engineering",
          prompt: "Generate comprehensive test cases for the given requirement or feature. Include test steps, expected outcomes, and edge cases.",
          model: "gpt-4o",
          inputs: ["requirement"],
          outputs: ["test_cases"]
        },
        {
          name: "Test Coverage Analyzer",
          description: "Analyzes test coverage and suggests improvements",
          category: "Software Engineering",
          prompt: "Analyze the provided test cases for coverage completeness. Identify gaps and suggest additional test scenarios to improve coverage.",
          model: "gpt-4o",
          inputs: ["test_cases"],
          outputs: ["coverage_analysis"]
        },
        {
          name: "User Story Estimator",
          description: "Estimates effort and complexity for user stories",
          category: "Software Engineering",
          prompt: "Estimate the effort and complexity for the given user story. Consider factors like technical complexity, dependencies, and testing requirements.",
          model: "gpt-4o",
          inputs: ["user_story"],
          outputs: ["estimation"]
        },
        {
          name: "Technical Debt Analyzer",
          description: "Analyzes code and identifies technical debt",
          category: "Software Engineering",
          prompt: "Analyze the provided code for signs of technical debt. Identify code smells, violations of best practices, and potential refactoring opportunities.",
          model: "gpt-4o",
          inputs: ["code"],
          outputs: ["analysis"]
        },
        {
          name: "Code Review Assistant",
          description: "Assists in code review by analyzing code quality",
          category: "Software Engineering",
          prompt: "Review the provided code for quality issues. Check for bugs, performance issues, security vulnerabilities, and adherence to best practices.",
          model: "gpt-4o",
          inputs: ["code"],
          outputs: ["review"]
        },
        {
          name: "API Documentation Generator",
          description: "Generates API documentation from code or specifications",
          category: "Software Engineering",
          prompt: "Generate comprehensive API documentation from the provided API specification or code. Include endpoints, parameters, request/response formats, and examples.",
          model: "gpt-4o",
          inputs: ["api_spec"],
          outputs: ["documentation"]
        },
        {
          name: "Performance Test Generator",
          description: "Generates performance test scenarios",
          category: "Software Engineering",
          prompt: "Generate performance test scenarios for the described system. Include load testing, stress testing, and endurance testing scenarios with specific metrics to measure.",
          model: "gpt-4o",
          inputs: ["system_description"],
          outputs: ["test_scenarios"]
        },
        {
          name: "Security Test Generator",
          description: "Generates security test cases and scenarios",
          category: "Software Engineering",
          prompt: "Generate security test cases for the described system. Include tests for common vulnerabilities like injection attacks, authentication issues, and access control problems.",
          model: "gpt-4o",
          inputs: ["system_description"],
          outputs: ["security_tests"]
        }
      ];
      
      // Show total number of blocks to create
      console.log(`Attempting to create ${demoBlocks.length} blocks`);
      
      // Create blocks with a small delay between each
      for (const block of demoBlocks) {
        try {
          console.log(`Creating block: ${block.name} (Category: ${block.category})`);
          await onCreateBlock(block);
          await new Promise(resolve => setTimeout(resolve, 150)); // Add delay between blocks
        } catch (err) {
          console.error(`Failed to create block ${block.name}:`, err);
          // Continue with other blocks even if one fails
        }
      }
      
      console.log("Finished creating demo blocks");
      // No need to explicitly fetch blocks again - onCreateBlock will trigger a refresh
    } catch (err) {
      console.error('Failed to create demo blocks:', err);
      setError('Failed to create demo blocks. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBlock) {
        await onUpdateBlock(editingBlock.id, { ...formData, id: editingBlock.id });
      } else {
        await onCreateBlock(formData);
      }
      resetForm();
    } catch (err) {
      console.error('Failed to save block:', err);
    }
  };

  const handleEdit = (block: Block) => {
    setEditingBlock(block);
    setFormData({
      name: block.name,
      description: block.description,
      category: block.category,
      prompt: block.prompt || '',
      model: block.model || (modelOptions.length > 0 ? modelOptions[0].id : ''),
      inputs: block.inputs || [],
      outputs: block.outputs || []
    });
    
    // Scroll to the editor form
    setTimeout(() => {
      if (editorFormRef.current) {
        editorFormRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleDeleteClick = async (block: Block) => {
    setDeleteLoading(true);
    try {
      // Check if the block is used in any workflows
      const usage = await api.getBlockUsage(block.id);
      
      setBlockToDelete(block);
      setUsedInWorkflows(usage.workflows);
      setIsDeleteModalOpen(true);
    } catch (err) {
      console.error('Failed to check block usage:', err);
      alert('Failed to check if block is used in any workflows. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!blockToDelete) return;
    
    try {
      // If block is used in workflows, remove it from all of them first
      if (usedInWorkflows.length > 0) {
        for (const workflow of usedInWorkflows) {
          // Create updated workflow without the block and its connections
          const updatedWorkflow = {
            ...workflow,
            blocks: workflow.blocks.filter(b => b.blockId !== blockToDelete.id),
            connections: workflow.connections.filter(conn => {
              // Get the blocks in this workflow
              const blocks = workflow.blocks;
              // Find source and target blocks
              const sourceBlock = blocks.find(b => b.id === conn.sourceId);
              const targetBlock = blocks.find(b => b.id === conn.targetId);
              // Keep connection only if neither source nor target is the block being deleted
              return !(sourceBlock?.blockId === blockToDelete.id || targetBlock?.blockId === blockToDelete.id);
            })
          };
          
          // Update the workflow
          await api.updateWorkflow(workflow.id, updatedWorkflow);
        }
      }
      
      // Now delete the block
      await onDeleteBlock(blockToDelete.id);
    } catch (err) {
      console.error('Failed to delete block:', err);
      throw err; // Re-throw to be caught by the DeleteBlockModal
    }
  };

  const resetForm = () => {
    // Find the current default model
    const defaultModel = modelOptions.find(option => option.isDefault);
    const defaultModelId = defaultModel ? defaultModel.id : (modelOptions.length > 0 ? modelOptions[0].id : '');
    
    setFormData({
      name: '',
      description: '',
      category: '',
      prompt: '',
      model: defaultModelId,
      inputs: [],
      outputs: []
    });
    setEditingBlock(null);
    setIsCreating(false);
  };

  // Add helper functions to handle inputs and outputs as comma-separated lists
  const handleInputsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputsText = e.target.value;
    const inputsArray = inputsText.split(',').map(input => input.trim()).filter(input => input !== '');
    setFormData({ ...formData, inputs: inputsArray });
  };

  const handleOutputsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const outputsText = e.target.value;
    const outputsArray = outputsText.split(',').map(output => output.trim()).filter(output => output !== '');
    setFormData({ ...formData, outputs: outputsArray });
  };

  // Add function to handle block testing
  const handleTestBlock = (block: Block) => {
    setTestBlockData({
      id: block.id,
      name: block.name,
      prompt: block.prompt || '',
      model: block.model
    });
    setIsTestModalOpen(true);
  };

  // Add function to save prompt after testing
  const handleSavePrompt = async (prompt: string, model?: string) => {
    try {
      const blockToUpdate = blocks.find(b => b.id === testBlockData.id);
      if (!blockToUpdate) return;
      
      const updatedBlock = {
        ...blockToUpdate,
        prompt,
        ...(model && { model })
      };
      
      await onUpdateBlock(blockToUpdate.id, updatedBlock);
    } catch (err) {
      console.error('Failed to save prompt:', err);
      alert('Failed to save prompt');
    }
  };

  const handleResetClick = () => {
    setIsResetModalOpen(true);
  };

  const handleAcceptOptimizedPrompt = (optimizedPrompt: string) => {
    setFormData({ ...formData, prompt: optimizedPrompt });
  };

  const handleResetConfirm = async () => {
    if (resetConfirmation !== 'DELETE') return;
    
    setIsResetting(true);
    try {
      // Delete all blocks except Start and Results
      // Only delete blocks that can be deleted (we own them)
      const deletePromises = [];
      const results = { successCount: 0, failureCount: 0 };
      
      for (const block of blocks) {
        if (block.name.toLowerCase() !== 'start' && block.name.toLowerCase() !== 'results') {
          deletePromises.push(
            onDeleteBlock(block.id)
              .then(() => {
                results.successCount++;
                console.log(`Successfully deleted block: ${block.name}`);
              })
              .catch((err) => {
                results.failureCount++;
                console.warn(`Failed to delete block "${block.name}": ${err.message}`);
                // Don't throw error, just log it - continue with other blocks
              })
          );
        }
      }
      
      // Wait for all deletion attempts to complete
      await Promise.allSettled(deletePromises);
      
      setIsResetModalOpen(false);
      setResetConfirmation('');
      
      // Show summary message
      if (results.failureCount === 0) {
        alert(`Successfully reset ${results.successCount} blocks.`);
      } else {
        alert(`Reset completed: ${results.successCount} blocks deleted, ${results.failureCount} blocks skipped (not owned by you or already deleted).`);
      }
    } catch (err) {
      console.error('Failed to reset blocks:', err);
      alert('Failed to reset blocks. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Error message display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
          <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
            </svg>
          </span>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Block Library</h1>
        <div className="flex gap-2">
          <button
            onClick={handleResetClick}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
            disabled={isLoading}
          >
            Reset My Blocks
          </button>
          <button
            onClick={handleDemoBlocks}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="animate-pulse">Creating...</span>
            ) : (
              "Inspire Me ✨"
            )}
          </button>
          <button
            onClick={() => {
              resetForm();
              setIsCreating(true);
              setTimeout(() => {
                if (editorFormRef.current) {
                  editorFormRef.current.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }, 100);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
            disabled={isLoading}
          >
            Create Block
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search blocks by name, description, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-500 mt-2">
            Found {displayBlocks.length} block{displayBlocks.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </p>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Reset Your Blocks</h2>
            <p className="text-gray-600 mb-4">
              This will delete all blocks that you own (except Start and Results). Blocks owned by other users will be skipped. 
              This action cannot be undone. Type DELETE to confirm.
            </p>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2 mb-4"
              placeholder="Type DELETE to confirm"
              value={resetConfirmation}
              onChange={(e) => setResetConfirmation(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsResetModalOpen(false);
                  setResetConfirmation('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isResetting}
              >
                Cancel
              </button>
              <button
                onClick={handleResetConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400"
                disabled={resetConfirmation !== 'DELETE' || isResetting}
              >
                {isResetting ? 'Resetting...' : 'Reset My Blocks'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(isCreating || editingBlock) && (
        <>
          {/* Special handling for Decision Router blocks */}
          {editingBlock && editingBlock.id === 'decision-router' ? (
            <div className="bg-white shadow rounded-lg p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Configure Decision Router</h3>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Note:</strong> Decision Router is a built-in block. Configuration should be done when adding it to a workflow.
                      The Decision Router uses AI to dynamically route workflow execution based on input analysis.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-700">Category:</span>
                  <span className="ml-2 px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                    {editingBlock.category}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Description:</span>
                  <p className="mt-1 text-sm text-gray-600">{editingBlock.description}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Usage:</span>
                  <ul className="mt-1 text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Drag the Decision Router into your workflow</li>
                    <li>Click on it to configure routes and decision logic</li>
                    <li>Connect different routes to different processing paths</li>
                    <li>AI will choose the appropriate route based on input</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <form 
              ref={editorFormRef}
              onSubmit={handleSubmit} 
              className="bg-white shadow rounded-lg p-6 space-y-4"
            >
              <div className="space-y-4">
                {/* Name field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    placeholder="Block name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
            
            {/* Description field */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                placeholder="Block description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            
            {/* Category field */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="category"
                name="category"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="">Select a category</option>
                <option value="Text">Text</option>
                <option value="Logic">Logic</option>
                <option value="Math">Math</option>
                <option value="Data">Data</option>
                <option value="Prompt">Prompt</option>
                <option value="AI">AI</option>
                <option value="Processing">Processing</option>
                <option value="Economics">Economics</option>
                <option value="Software Engineering">Software Engineering</option>
              </select>
            </div>
            
            {/* Prompt field - shown for all blocks */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
                  Prompt Template
                </label>
                <button
                  type="button"
                  onClick={() => setOptimizationModalOpen(true)}
                  disabled={!formData.prompt || formData.prompt.trim() === ''}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Optimize this prompt using AI"
                >
                  <AutoFixHighIcon style={{ fontSize: 16, marginRight: 4 }} />
                  Optimize
                </button>
              </div>
              <textarea
                id="prompt"
                name="prompt"
                className="w-full p-3 border border-gray-300 rounded-md text-sm font-mono h-40"
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="Enter your prompt template here..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This is the system prompt that will be used to process your input. Write clear instructions for the AI.
              </p>
            </div>

            {/* Model selection dropdown */}
            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                Default Model
              </label>
              <Autocomplete
                fullWidth
                options={modelOptions}
                value={modelOptions.find((option) => option.id === formData.model) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setFormData({ ...formData, model: newValue.id });
                  }
                }}
                getOptionLabel={(option) => {
                  const label = option.displayName || option.name;
                  return option.deprecated ? `${label} (DEPRECATED)` : label;
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchLower = inputValue.toLowerCase();
                  return options.filter((option) =>
                    option.name.toLowerCase().includes(searchLower) ||
                    (option.displayName && option.displayName.toLowerCase().includes(searchLower)) ||
                    (option.provider && option.provider.toLowerCase().includes(searchLower))
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search models..."
                    variant="outlined"
                    size="small"
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: 400 }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                The default AI model to use when testing or executing this block.
              </p>
              {(() => {
                const selectedModel = modelOptions.find(option => option.id === formData.model);
                return selectedModel?.deprecated ? (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-sm text-yellow-800 font-medium">
                        This model is deprecated and may not work properly. Consider switching to a newer model.
                      </span>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Inputs field */}
            <div>
              <label htmlFor="inputs" className="block text-sm font-medium text-gray-700">
                Inputs (comma-separated)
              </label>
              <input
                type="text"
                id="inputs"
                name="inputs"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                placeholder="e.g., text, number, data"
                value={formData.inputs.join(', ')}
                onChange={handleInputsChange}
              />
            </div>
            
            {/* Outputs field */}
            <div>
              <label htmlFor="outputs" className="block text-sm font-medium text-gray-700">
                Outputs (comma-separated)
              </label>
              <input
                type="text"
                id="outputs"
                name="outputs"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                placeholder="e.g., text, result, processed"
                value={formData.outputs.join(', ')}
                onChange={handleOutputsChange}
              />
            </div>
            
            {/* Submit button */}
            <div className="flex justify-end">
              <button
                type="button"
                className="mr-2 bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-md shadow-sm px-4 py-2 text-sm font-medium"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-md shadow-sm px-4 py-2 text-sm font-medium"
              >
                {editingBlock ? 'Update Block' : 'Create Block'}
              </button>
            </div>
          </div>
        </form>
          )}
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {displayBlocks.map((block) => (
          <div key={block.id} className="bg-white shadow rounded-lg p-6 flex flex-col">
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-semibold text-gray-800">{block.name}</h2>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  block.category === 'Software Engineering' ? 'bg-red-100 text-red-800' :
                  block.category === 'Economics' ? 'bg-green-100 text-green-800' :
                  block.category === 'AI' ? 'bg-purple-100 text-purple-800' :
                  block.category === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {block.category}
                </span>
              </div>
              <p className="text-sm text-gray-600 my-3">{block.description}</p>
              
              {block.inputs && block.inputs.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Inputs:</h3>
                  <div className="flex flex-wrap mt-1 gap-1">
                    {block.inputs.map((input, index) => (
                      <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                        {input}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {block.outputs && block.outputs.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Outputs:</h3>
                  <div className="flex flex-wrap mt-1 gap-1">
                    {block.outputs.map((output, index) => (
                      <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                        {output}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {block.prompt && (
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Prompt:</h3>
                  <div className="mt-1 text-xs font-mono bg-gray-50 p-2 rounded-md border border-gray-200 max-h-24 overflow-y-auto">
                    {block.prompt}
                  </div>
                </div>
              )}
              
              {block.model && (
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Model:</h3>
                  <div className={`mt-1 text-xs p-2 rounded-md border inline-block ${(() => {
                    const modelOption = modelOptions.find((option: {id: string, name: string, deprecated?: boolean}) => option.id === block.model);
                    if (!modelOption) return 'bg-red-50 text-red-700 border-red-200'; // Unknown model
                    if (modelOption.deprecated) return 'bg-yellow-50 text-yellow-700 border-yellow-200'; // Deprecated model
                    return 'bg-green-50 text-green-700 border-green-200'; // Normal model
                  })()}`}>
                    {(() => {
                      // Find the model display name from our loaded model options
                      const modelOption = modelOptions.find((option: {id: string, name: string, deprecated?: boolean}) => option.id === block.model);
                      if (!modelOption) {
                        return (
                          <span>
                            {block.model || 'No model selected'}
                            <span className="ml-1 text-xs bg-red-100 text-red-800 px-1 rounded">UNKNOWN</span>
                          </span>
                        );
                      }
                      return (
                        <span>
                          {modelOption.name}
                          {modelOption.deprecated && (
                            <span className="ml-1 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">DEPRECATED</span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
              <div>
                <button
                  onClick={() => handleEdit(block)}
                  className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteClick(block)}
                  className="text-red-600 hover:text-red-800 text-sm"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Checking...' : 'Delete'}
                </button>
              </div>
              
              <button
                onClick={() => handleTestBlock(block)}
                className="px-3 py-1.5 rounded text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 flex items-center"
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Test
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Test Modal */}
      <PromptTestModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        blockId={testBlockData.id}
        blockName={testBlockData.name}
        initialPrompt={testBlockData.prompt}
        defaultModel={testBlockData.model}
        onSavePrompt={handleSavePrompt}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteBlockModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setBlockToDelete(null);
          setUsedInWorkflows([]);
        }}
        block={blockToDelete}
        usedInWorkflows={usedInWorkflows}
        onConfirmDelete={handleConfirmDelete}
      />

      {/* Prompt Optimization Modal */}
      <PromptOptimizationModal
        open={optimizationModalOpen}
        originalPrompt={formData.prompt || ''}
        onClose={() => setOptimizationModalOpen(false)}
        onAccept={handleAcceptOptimizedPrompt}
      />
    </div>
  );
}; 